import React from "react";
import { Link } from "react-router-dom";

// Standalone educational guides. Each is a self-contained article teaching a
// piece of comic-making craft, written to be useful on its own and to earn
// organic search traffic — not just UI documentation. Bodies are JSX so they
// can link to the editor and to each other. Rendered by GuidePage; listed by
// GuidesIndexPage; metadata is surfaced to pageMeta.js for pre-rendering.

const EditorLink = ({ children = "the editor" }) => (
  <Link to="/editor" style={{ color: "#00a4e4" }}>{children}</Link>
);

const G = (slug, children) => (
  <Link to={`/guides/${slug}`} style={{ color: "#00a4e4" }}>{children}</Link>
);

const StoryboardLink = ({ children = "Striply's storyboard maker" }) => (
  <Link to="/storyboard" style={{ color: "#00a4e4" }}>{children}</Link>
);

// A storyboard template page
const T = (slug, children) => (
  <Link to={`/storyboard-templates/${slug}`} style={{ color: "#00a4e4" }}>{children}</Link>
);

// Guides are grouped on the index by cluster
export const GUIDE_CLUSTERS = [
  { key: "comics", title: "Making comics" },
  { key: "storyboards", title: "Storyboards & film" },
];

export const GUIDES = [
  {
    slug: "how-to-lay-out-a-comic-page",
    title: "How to Lay Out a Comic Page",
    icon: "dashboard",
    readingTime: "6 min read",
    description:
      "Learn how to lay out a comic page: choosing a panel grid, guiding the reader's eye, using gutters, and shaping the flow of a scene from establishing shot to climax.",
    summary:
      "The fundamentals of turning a blank page into a readable sequence of panels — grids, reading order, gutters, and pacing your beats.",
    related: ["comic-panel-composition-and-pacing", "manga-vs-western-comic-layouts"],
    body: (
      <>
        <p>
          A comic page is not just a container for drawings — it is a reading
          experience. Before you draw a single figure, the arrangement of your
          panels already tells the reader how fast to move, where to look, and
          what matters most. Getting the layout right is the difference between
          a page that flows and one that confuses.
        </p>

        <h2>Start with the grid</h2>
        <p>
          Almost every comic page begins life as a grid. A simple, regular grid —
          two rows of two, or three rows of three — is not a limitation; it is a
          reliable rhythm the reader can settle into. The classic{" "}
          <strong>nine-panel grid</strong> (3&nbsp;×&nbsp;3) gives you a steady,
          clockwork cadence, while a <strong>2&nbsp;×&nbsp;3</strong> six-panel
          page reads a little more spaciously. When you open{" "}
          <EditorLink />, pick a grid from the <strong>Layout</strong> dropdown
          and treat it as scaffolding you can break later.
        </p>
        <p>
          The number of panels sets the tempo. Fewer, larger panels feel slow and
          weighty; more, smaller panels feel quick and busy. A good rule of thumb
          for a standard page is between four and eight panels — enough to develop
          a moment without cramming.
        </p>
        <p>
          Planning a film or video rather than a comic? The same thinking about
          beats and reading order applies to a storyboard, with one difference:
          every frame shares the camera's aspect ratio. See{" "}
          {G("what-is-a-storyboard", "what a storyboard is and how to make one")}.
        </p>

        <h2>Guide the reader's eye</h2>
        <p>
          In left-to-right languages, readers travel the page in a{" "}
          <strong>Z-pattern</strong>: left to right across the top row, then down
          and back to the left to begin the next. Every panel should hand off to
          the next one along this path. The most common layout mistake is
          ambiguity — two panels sitting side by side at the same height with no
          clear order, so the reader stalls, unsure which to read first.
        </p>
        <p>
          When you want to break the grid, break it deliberately. A panel that
          spans the full width of the page acts as a natural stop, resetting the
          eye before the next row. A tall panel on the far right can carry the eye
          down into the following tier.
        </p>

        <h2>Mind the gutters</h2>
        <p>
          The <strong>gutter</strong> is the empty space between panels, and it
          does real work. Narrow gutters make moments feel connected and
          continuous; wide gutters introduce a beat of separation, a small pause
          in time. Keep your gutters consistent within a page unless you have a
          reason to vary them — irregular spacing reads as an accident rather than
          a choice. In Striply the gutter width is a single slider, and it also
          drives the snap grid, so tightening the gutters tightens your alignment
          spacing too.
        </p>

        <h2>Shape the scene</h2>
        <p>
          Think of a page as a small piece of music with a beginning, middle, and
          end. A conventional shape is an <strong>establishing panel</strong> that
          sets the location, a run of <strong>beat panels</strong> that carry the
          action or dialogue, and a <strong>climax or turn</strong> — often the
          largest panel, saved for last — that lands the moment you have been
          building toward. Sizing your panels to match their importance is the
          single most powerful pacing tool you have, and it is the subject of the
          next guide on {G("comic-panel-composition-and-pacing", "composition and pacing")}.
        </p>

        <p>
          Once the skeleton reads clearly, everything else — art, dialogue, sound
          effects — hangs off it. Open <EditorLink /> and try blocking out a page
          with nothing but empty panels first; if the empty page already reads in
          the right order, you are on solid ground.
        </p>
      </>
    ),
  },

  {
    slug: "comic-panel-composition-and-pacing",
    title: "Comic Panel Composition & Pacing",
    icon: "aspect_ratio",
    readingTime: "6 min read",
    description:
      "How panel size, shape, and spacing control the pacing of a comic. Learn to use wide panels, splash pages, and the widescreen style to slow down or speed up time.",
    summary:
      "Panel size is time. A practical look at how the shape and rhythm of your panels controls how fast a scene reads.",
    related: ["how-to-lay-out-a-comic-page", "how-to-letter-a-comic"],
    body: (
      <>
        <p>
          Comics have a peculiar superpower: they control time with space. A
          reader spends longer on a big panel than a small one, and the gap
          between two panels can represent a heartbeat or a hundred years. Once
          you understand that <strong>panel size is time</strong>, composition
          stops being decoration and becomes storytelling.
        </p>

        <h2>Big panels slow time down</h2>
        <p>
          A large panel demands attention. The reader lingers, taking in detail,
          and the moment feels weighty. That is why the emotional peak of a scene —
          the reveal, the kiss, the punch — usually gets the biggest panel on the
          page. The extreme version is the <strong>splash page</strong>: a single
          full-page panel used for maximum impact, often a dramatic entrance or a
          sweeping establishing shot.
        </p>

        <h2>Small panels speed it up</h2>
        <p>
          Rows of small, equally sized panels create a fast, ticking rhythm.
          They are perfect for rapid dialogue, a quick exchange of glances, or a
          burst of action beats in close succession. A cluster of tiny panels
          followed by one large one is a classic setup-and-payoff move: the small
          panels build tension, and the big one releases it.
        </p>

        <h2>The shape of a panel is a camera</h2>
        <p>
          A wide, letterbox panel behaves like a cinematic establishing shot — it
          takes in a landscape, a room, a crowd. A tall, narrow panel emphasizes
          height and can create unease or grandeur: a towering figure, a fall, a
          skyscraper. Square panels feel neutral and stable. When you resize a
          panel in <EditorLink />, you are effectively choosing a camera lens for
          that moment. Striply also lets you change a panel's{" "}
          <strong>shape</strong> entirely — circles and diamonds can signal a
          flashback, a dream, or a change in emotional register.
        </p>
        <p>
          Film storyboard artists make exactly the same choice with shot sizes —
          wide, medium, close-up — and the vocabulary transfers directly between
          the two crafts. Our {G("camera-shot-types", "visual guide to camera shot types")}{" "}
          breaks each one down.
        </p>

        <h2>Vary the rhythm</h2>
        <p>
          The most common pacing mistake is monotony: a whole page of
          identically sized panels reads flat, no matter how good the art is. Good
          pages breathe. They alternate between tight and open, fast and slow.
          Look at a page you admire and notice how rarely two adjacent panels are
          the same size — the variation <em>is</em> the pacing.
        </p>
        <p>
          A popular modern approach is the <strong>widescreen</strong> style,
          which stacks a few wide horizontal panels down the page. It reads slowly
          and cinematically, giving each beat room to land. Contrast that with a
          dense European or manga grid, where many small panels pack a lot of
          information into a single page. Neither is better — they are different
          tempos, and knowing both lets you choose.
        </p>

        <h2>Composition inside the panel</h2>
        <p>
          Pacing is about the panels; composition is about what happens inside
          them. Keep the important element — a face, a hand, an object — near where
          the eye enters the panel, and leave space in the direction of movement so
          the action has somewhere to go. Remember to reserve room for dialogue:
          nothing sabotages a beautiful drawing like a speech bubble slapped over
          the character's face. Planning that space is where{" "}
          {G("how-to-letter-a-comic", "lettering")} and layout meet.
        </p>

        <p>
          Try it directly: open <EditorLink /> and lay out the same four-beat
          scene twice — once with four equal panels, once with three small panels
          and one large one — and read them back. The story is the same; the
          feeling is not.
        </p>
      </>
    ),
  },

  {
    slug: "how-to-letter-a-comic",
    title: "How to Letter a Comic: Speech Bubbles & Dialogue",
    icon: "mode_comment",
    readingTime: "7 min read",
    description:
      "A guide to comic lettering: reading order of speech bubbles, tail direction, bubble shapes for thoughts and shouts, font choice, and the ALL CAPS convention.",
    summary:
      "Lettering is invisible when it's done well and glaring when it isn't. Reading order, tails, bubble shapes, and font conventions that keep dialogue clear.",
    related: ["comic-sound-effects-sfx", "comic-panel-composition-and-pacing"],
    body: (
      <>
        <p>
          Lettering is the most under-appreciated craft in comics. When it is done
          well, readers never notice it — the words simply flow into their heads in
          the right order. When it is done badly, it stops the story cold. Here are
          the conventions that keep dialogue readable.
        </p>

        <h2>Reading order comes first</h2>
        <p>
          Within a panel, speech bubbles are read the same way as the page:{" "}
          <strong>top to bottom, left to right</strong>. That means the first
          person to speak gets the upper-left bubble, and the conversation flows
          down and across. If two characters talk and you place the replier's
          bubble above the speaker's, you force the reader to jump backward — a
          small snag that accumulates into confusion over a page. Plan your bubble
          positions before you finalize the art, and leave room for them at the top
          of the panel where they naturally sit.
        </p>

        <h2>Tails point to the speaker</h2>
        <p>
          The <strong>tail</strong> is the little pointer that connects a bubble to
          whoever is talking. It should aim clearly at the speaker's head or mouth,
          and — importantly — <strong>tails should never cross</strong>. Crossed
          tails are the visual equivalent of two people talking over each other; the
          reader can't tell who said what. In Striply you can drag the tail tip to
          the speaker and even add a second tail from one bubble when two
          characters say the same thing in unison.
        </p>

        <h2>Bubble shape carries meaning</h2>
        <p>
          The outline of a bubble tells the reader <em>how</em> a line is spoken,
          before they read a word:
        </p>
        <ul>
          <li>
            <strong>Rounded / oval</strong> — normal speech. Your default.
          </li>
          <li>
            <strong>Cloud (thought bubble)</strong> — internal thoughts, unspoken.
          </li>
          <li>
            <strong>Spiky / burst</strong> — shouting, alarm, or a voice over a
            phone or radio.
          </li>
          <li>
            <strong>Dashed / wavy (whisper)</strong> — quiet or trailing-off
            speech.
          </li>
          <li>
            <strong>Rectangular caption box</strong> — narration or a character's
            voice-over, usually placed at a corner of the panel rather than tailed
            to a mouth.
          </li>
        </ul>
        <p>
          Striply offers all of these as shape presets, so you can switch a
          bubble from speech to thought to shout without redrawing it.
        </p>

        <h2>Choose a legible font</h2>
        <p>
          Comic lettering has its own typographic traditions. The text should be a{" "}
          <strong>clean, comic-style face</strong> — never a stiff office font like
          Times or Arial, which read as flat and lifeless in a hand-drawn medium.
          Fonts like Comic Neue, Bangers, or a hand-lettered style sit far more
          naturally. Keep dialogue around 12–16&nbsp;px so it is comfortable at
          reading size, and keep it consistent from panel to panel.
        </p>

        <h2>Why comics use ALL CAPS</h2>
        <p>
          Traditional Western comics letter dialogue in{" "}
          <strong>all capitals</strong>, and it is not an accident. Uppercase
          hand-lettering is more uniform and easier to read at small sizes, avoids
          the ambiguity of a lowercase &ldquo;l&rdquo; versus a capital
          &ldquo;I,&rdquo; and gives the classic comic-book look. There is one
          famous exception: a lowercase &ldquo;i&rdquo; is sometimes kept for
          emphasis. If you want an authentic Western feel, turn on the ALL CAPS
          option; manga and many webcomics use mixed case instead, so match the
          convention to your style.
        </p>

        <h2>Give the words room to breathe</h2>
        <p>
          Leave a comfortable margin of empty space inside each bubble — text
          jammed against the outline feels claustrophobic. Break lines so the
          bubble forms a pleasing oval rather than a long sausage, and never let a
          bubble grow so large it swallows the art. Lettering and{" "}
          {G("how-to-lay-out-a-comic-page", "layout")} are partners: the best pages
          plan for the words from the very first sketch.
        </p>

        <p>
          Practice on a real panel: open <EditorLink />, add two characters'
          worth of dialogue, and check that a stranger could read it in the right
          order without you explaining it.
        </p>
      </>
    ),
  },

  {
    slug: "comic-sound-effects-sfx",
    title: "Sound Effects in Comics: A Guide to SFX & Onomatopoeia",
    icon: "text_fields",
    readingTime: "5 min read",
    description:
      "How to use sound effects and onomatopoeia in comics — matching lettering style to the sound, using color and stroke, sizing SFX for volume, and placement.",
    summary:
      "BAM, WHOOSH, drip. How comic sound effects work, and how to letter onomatopoeia so it reads as loud, soft, sharp, or smooth.",
    related: ["how-to-letter-a-comic", "manga-vs-western-comic-layouts"],
    body: (
      <>
        <p>
          Comics are a silent medium that is full of noise. The gunshots, crashes,
          and whooshes all live in the lettering — the stylized{" "}
          <strong>sound effects</strong> (SFX) splashed across a panel. Done well,
          they are half sound and half illustration.
        </p>

        <h2>Onomatopoeia: words that sound like sounds</h2>
        <p>
          Sound effects are built on <strong>onomatopoeia</strong> — words that
          imitate the noise they describe. Some are conventional (BAM, POW, CRASH,
          THUD), but the best are invented to fit the exact moment: a long{" "}
          <em>SHhhhhk</em> for a blade leaving its sheath, a soft{" "}
          <em>fwump</em> for a body hitting a mattress. Do not be afraid to coin
          your own; readers &ldquo;hear&rdquo; them instantly.
        </p>

        <h2>Let the lettering imitate the sound</h2>
        <p>
          The shape of the letters should carry the quality of the noise:
        </p>
        <ul>
          <li>
            <strong>Sharp, jagged edges</strong> for hard, sudden sounds — a{" "}
            CRACK, a SMASH, breaking glass.
          </li>
          <li>
            <strong>Smooth, flowing, italic letters</strong> for continuous or
            soft sounds — a WHOOSH, a hummmm, wind.
          </li>
          <li>
            <strong>Dripping or bubbly forms</strong> for liquid sounds.
          </li>
          <li>
            <strong>Thin, small lettering</strong> for quiet sounds; heavy, bold,
            huge lettering for loud ones.
          </li>
        </ul>
        <p>
          In Striply, SFX are a separate kind of text item — you can rotate them,
          stretch them independently in width and height, and give them their own
          font, so the same word can be squashed, skewed, or blown up to match its
          volume.
        </p>

        <h2>Size equals volume</h2>
        <p>
          The single clearest signal of loudness is <strong>size</strong>. A
          whispered <em>tik</em> might be a few small letters in a corner; an
          explosion's <em>KA-BOOM</em> can dominate the entire panel, even
          spilling across gutters into neighboring panels for a truly enormous
          blast. Scale is your volume knob — use the full range.
        </p>

        <h2>Color and stroke make it pop</h2>
        <p>
          Classic comic SFX use a <strong>bold fill with a contrasting
          outline</strong> — think yellow letters with a heavy black stroke, or
          red with white. That two-tone treatment is what makes sound effects leap
          off the page and read instantly as &ldquo;sound&rdquo; rather than
          dialogue. Striply gives SFX independent fill and stroke colors for exactly
          this reason. Match the color to the mood: hot reds and yellows for
          impacts, cool blues for cold or electronic sounds.
        </p>

        <h2>Placement matters</h2>
        <p>
          Put the effect near its source — the SFX for a slamming door belongs by
          the door, not floating in the sky. Angle it along the direction of the
          action so it feels like part of the motion. But keep it clear of your{" "}
          {G("how-to-letter-a-comic", "speech bubbles")}; a sound effect that
          collides with dialogue muddies both.
        </p>

        <p>
          Add a <strong>BAM!</strong> to a panel in <EditorLink />, then try
          stretching it, rotating it, and swapping its colors — you will feel how
          much the same word can change.
        </p>
      </>
    ),
  },

  {
    slug: "manga-vs-western-comic-layouts",
    title: "Manga vs. Western Comic Layouts",
    icon: "grain",
    readingTime: "6 min read",
    description:
      "The key differences between manga and Western comic layouts: right-to-left reading, page size, panel density, screentones versus color, and pacing conventions.",
    summary:
      "Right-to-left reading, screentones, B5 pages, and looser grids — what actually separates a manga page from a Western one, and how to work in either.",
    related: ["how-to-lay-out-a-comic-page", "comic-sound-effects-sfx"],
    body: (
      <>
        <p>
          Manga and Western comics tell stories with the same basic tools — panels,
          gutters, bubbles — but the traditions around them differ in ways that
          shape the whole reading experience. If you want your page to feel like
          one or the other, these are the details that matter.
        </p>

        <h2>Reading direction</h2>
        <p>
          The biggest difference is direction. Western comics read{" "}
          <strong>left to right</strong>; traditional manga reads{" "}
          <strong>right to left</strong>. On a manga page the first panel is in the
          top-<em>right</em> corner, and the eye travels leftward across each row.
          Everything else — the placement of the first speech bubble, which way
          characters face, where the &ldquo;forward&rdquo; direction of action
          points — flips accordingly. If you are drawing manga-style, commit to it
          fully; a page that mixes directions is disorienting.
        </p>

        <h2>Page size and format</h2>
        <p>
          Western comics are usually drawn for <strong>Letter</strong> or a
          US comic trim, while manga is traditionally printed at{" "}
          <strong>B5</strong> or the smaller tankōbon volume size. The aspect ratio
          affects how many panels sit comfortably on a page and how tall your
          panels can be. Striply includes Letter, A4, and manga B5 as page-size
          presets so you can start from the right canvas.
        </p>

        <h2>Panel density and pacing</h2>
        <p>
          Western superhero comics have leaned toward relatively regular grids and,
          in recent decades, the slower <strong>widescreen</strong> style with a
          few big panels per page. Manga tends toward{" "}
          <strong>looser, more varied layouts</strong> — panels that tilt, overlap,
          break their borders, and vary wildly in size within a single page. Manga
          also tends to &ldquo;decompress,&rdquo; spending many panels and even
          whole pages on a single beat to build atmosphere, where a Western comic
          might cover the same ground in two panels. Neither is more correct; they
          are different rhythms of storytelling. (For more on rhythm, see{" "}
          {G("comic-panel-composition-and-pacing", "composition and pacing")}.)
        </p>

        <h2>Screentones versus color</h2>
        <p>
          Most manga is printed in <strong>black and white</strong>, and it uses{" "}
          <strong>screentones</strong> — sheets of halftone dots, lines, and
          gradients — to create shading and texture without ink washes. A skilled
          manga artist builds an entire tonal world out of dot patterns.
          Mainstream Western comics, by contrast, are usually{" "}
          <strong>full color</strong>. Striply supports screentone overlays —
          dots, lines, and crosshatch — on any panel, so you can get that
          black-and-white manga texture, or leave panels in color for a Western
          look.
        </p>

        <h2>Gutters and sound effects</h2>
        <p>
          Small conventions add up. Manga frequently uses{" "}
          <strong>black gutters</strong> for flashbacks or to change the emotional
          register, where Western pages default to white. Manga sound effects are
          often integrated into the art as flowing, hand-drawn kana, while Western{" "}
          {G("comic-sound-effects-sfx", "SFX")} tend to be bold, blocky, colored
          lettering. You can lean either way in Striply by styling your SFX and
          gutter colors to taste.
        </p>

        <p>
          Pick a lane and try it: open <EditorLink />, choose the B5 page size, add
          a screentone to a panel, and lay the page out right-to-left. Then do a
          Western version and compare how differently they read.
        </p>
      </>
    ),
  },

  {
    slug: "preparing-comics-for-print",
    title: "Preparing Your Comic for Print: Bleed, Safe Margins & DPI",
    icon: "download",
    readingTime: "6 min read",
    description:
      "A practical print-prep guide for comics: what DPI to export, how bleed and safe margins work, RGB vs CMYK, choosing page size, and exporting a print-ready PDF.",
    summary:
      "Everything that separates a screen file from a print-ready one — resolution, bleed, safe margins, color, and how to export a PDF a printer will accept.",
    related: ["how-to-lay-out-a-comic-page", "manga-vs-western-comic-layouts"],
    body: (
      <>
        <p>
          A comic that looks perfect on screen can come back from the printer with
          fuzzy art, cut-off panels, or muddy colors. Print has its own rules, and
          they are worth learning before you send a file off. Here is what actually
          matters.
        </p>

        <h2>Resolution: export at 300 DPI</h2>
        <p>
          <strong>DPI</strong> (dots per inch) is the density of detail in your
          file at print size. Screens are happy at 72–150 DPI, but professional
          printing needs <strong>300 DPI</strong> — below that, line art looks soft
          and jagged in print. For fine detail or very crisp black-and-white line
          work, some artists export at 600 DPI. When you export from Striply, choose
          150 for screen sharing, <strong>300 for standard print</strong>, and 600
          for the highest-quality print jobs.
        </p>

        <h2>Bleed: extend art past the edge</h2>
        <p>
          Printers cannot cut paper with perfect precision, so any art meant to run
          to the very edge of the page must extend <em>past</em> where the page
          will be trimmed. That extra margin is the <strong>bleed</strong>,
          typically about 1/8&nbsp;inch (3&nbsp;mm) on every side. Without bleed, a
          slightly misaligned cut leaves a thin white sliver along the edge. If your
          panels or backgrounds are supposed to reach the paper's edge, make sure
          they run all the way into the bleed area.
        </p>

        <h2>Safe margins: keep the important stuff inside</h2>
        <p>
          The mirror image of bleed is the <strong>safe area</strong> (or safe
          margin): a smaller boundary inside the trim line. Because the cut can
          drift, keep anything you cannot afford to lose — dialogue, faces, page
          numbers — comfortably inside the safe margin, away from the edge. The rule
          of thumb: bleed for what should run off the page, safe margin for what
          must never be cut.
        </p>

        <h2>A note on color: RGB vs CMYK</h2>
        <p>
          Screens display color in <strong>RGB</strong> (red, green, blue), while
          most printing presses use <strong>CMYK</strong> (cyan, magenta, yellow,
          black) inks. Some bright RGB colors — especially vivid blues and
          greens — simply cannot be reproduced in CMYK and will shift or dull in
          print. If you are printing professionally, ask your printer whether they
          want a CMYK file, and avoid relying on the most saturated screen colors for
          anything critical. For home printing and digital reading, RGB is fine.
        </p>

        <h2>Choose the right page size</h2>
        <p>
          Decide your final trim size <em>before</em> you lay out pages, because it
          affects everything from panel proportions to margins. Common choices are{" "}
          <strong>US Letter</strong>, <strong>A4</strong>, and manga{" "}
          <strong>B5</strong> — all available as presets in Striply. If your printer
          expects a specific size, set it up front so you are not rescaling finished
          artwork later. Page size also shapes your{" "}
          {G("how-to-lay-out-a-comic-page", "layout")} decisions.
        </p>

        <h2>Export a print-ready PDF</h2>
        <p>
          <strong>PDF</strong> is the standard delivery format for print because it
          preserves layout, resolution, and vector text reliably across machines.
          When your pages are finished, export each one to PDF at 300 DPI at your
          chosen page size. It is also worth keeping a working copy of your project —
          Striply lets you export your layout as JSON so you can reopen and revise a
          page later without starting over.
        </p>

        <p>
          When you are ready, open <EditorLink />, set your page size and DPI, and
          export a test PDF. Print one page at home first — proofing on paper catches
          problems no screen will show you.
        </p>
      </>
    ),
  },
  {
    slug: "what-is-a-storyboard",
    cluster: "storyboards",
    title: "What Is a Storyboard? How to Make One, Step by Step",
    icon: "movie",
    readingTime: "7 min read",
    description:
      "What a storyboard is, what goes in each frame, and how to make one step by step — from script to shot list to finished boards — for films, videos, ads, and animation.",
    summary:
      "What storyboards are for, what every frame should say, and a step-by-step process from script to finished boards.",
    related: ["camera-shot-types", "storyboard-aspect-ratios"],
    body: (
      <>
        <p>
          A <strong>storyboard</strong> is a sequence of drawings that shows, shot
          by shot, how a film, video, ad, or animation will play out. Each frame is
          one shot: what the camera sees, how big the subject is in frame, how the
          camera moves, and what is said or heard. Read in order, the boards let
          everyone on a production see the finished piece before a single
          minute is filmed.
        </p>

        <h2>Why productions storyboard</h2>
        <p>
          Boards are a communication tool first and a drawing exercise second.
          A director uses them to explain coverage to the cinematographer; a
          producer uses them to estimate how many setups a day will need; an
          editor uses them to understand how scenes are meant to cut together.
          They are also the cheapest place to find problems. Discovering that a
          scene needs a reverse angle you never planned costs an eraser on paper
          and half a day on set.
        </p>
        <p>
          Storyboards are not only for big productions. A YouTube creator planning
          a ten-shot intro, an agency pitching a thirty-second spot, and a student
          shooting a short all benefit from deciding the shots before the camera
          is rolling. You do not need to draw well — clear stick figures with good
          notes beat beautiful drawings that leave the shot ambiguous.
        </p>

        <h2>What goes in a storyboard frame</h2>
        <ul>
          <li>
            <strong>The image</strong> — the framing of the shot, drawn inside a
            frame that matches your delivery{" "}
            {G("storyboard-aspect-ratios", "aspect ratio")} (16:9 for most
            video, 2.39:1 for widescreen film, 9:16 for vertical social video).
          </li>
          <li>
            <strong>A shot number</strong>, so everyone can refer to "shot 14"
            without ambiguity. Many productions number by scene and shot (12A,
            12B).
          </li>
          <li>
            <strong>The shot type</strong> — wide, medium, close-up, over the
            shoulder. Our {G("camera-shot-types", "guide to shot types")} covers
            the standard abbreviations.
          </li>
          <li>
            <strong>Camera movement</strong> — a pan, a push in, a crane up —
            usually drawn as arrows on the frame and written in the notes.
          </li>
          <li>
            <strong>Action and dialogue</strong> — a line or two describing what
            happens, plus the lines, voice-over, music, or sound cues that play
            over the shot.
          </li>
          <li>
            <strong>Duration</strong>, especially for ads and animation, where
            the total running time is fixed.
          </li>
        </ul>

        <h2>How to make a storyboard, step by step</h2>
        <p>
          <strong>1. Break the script into beats.</strong> Read the scene and mark
          every moment where something changes: a new piece of information, a
          reaction, a movement, a cut in location. Each beat is a candidate shot.
        </p>
        <p>
          <strong>2. Write a rough shot list.</strong> For each beat, decide the
          shot size and angle in words before drawing anything: "WS — Maya exits
          the elevator", "CU — her hand on the keycard". It is much faster to
          rearrange words than drawings.
        </p>
        <p>
          <strong>3. Choose your format.</strong> Pick the aspect ratio of the
          final video and a board layout. A sheet of six 16:9 frames is the
          workhorse; thumbnail sheets with nine small frames are good for a first
          pass; one frame per page suits client presentations. Ready-made layouts
          like the {T("16-9-widescreen", "16:9 widescreen template")} and the{" "}
          {T("vertical-9-16", "9:16 vertical template")} save you the setup.
        </p>
        <p>
          <strong>4. Thumbnail quickly.</strong> Sketch every frame small and
          fast. You are solving staging — who is where, which way they face, how
          one shot leads into the next — not rendering.
        </p>
        <p>
          <strong>5. Refine and annotate.</strong> Redraw the frames that matter,
          add movement arrows, and fill in the notes. Check screen direction: if a
          character exits frame right, they should usually enter the next shot
          from frame left, or the cut will feel like a jump.
        </p>
        <p>
          <strong>6. Share and revise.</strong> Export the boards as a PDF for the
          team, collect notes, and rework. Boards are a draft of the edit, so expect
          them to change.
        </p>

        <h2>Storyboards, animatics, and comics</h2>
        <p>
          An <strong>animatic</strong> is a storyboard cut to time — the frames
          played in sequence with the soundtrack, so you can feel the pacing.
          Animation studios lean on animatics heavily because every second of
          finished animation is expensive. Comics share a lot of DNA with
          storyboards: both break a story into framed moments. The difference is
          that a comic page is designed to be read as a finished page, with panels
          of many shapes, while a storyboard is a plan for moving pictures where
          every frame shares the camera's shape. If you make comics, our guide on{" "}
          {G("how-to-lay-out-a-comic-page", "laying out a comic page")} covers
          the page-design side.
        </p>

        <p>
          Ready to try it? <StoryboardLink>Start a storyboard in Striply</StoryboardLink>{" "}
          — choose a frame ratio, sketch or drop images into the frames, type
          notes directly under each shot, and export a print-ready PDF. It is free
          and needs no account.
        </p>
      </>
    ),
  },

  {
    slug: "camera-shot-types",
    cluster: "storyboards",
    title: "Camera Shot Types for Storyboards: Sizes, Angles & Moves",
    icon: "videocam",
    readingTime: "8 min read",
    description:
      "A storyboard artist's guide to camera shot types: shot sizes from extreme wide to extreme close-up, camera angles, framing like OTS and POV, camera moves, and the abbreviations used on boards.",
    summary:
      "Shot sizes, angles, framings, and camera moves — what each one does to an audience, and how to note it on a board.",
    related: ["what-is-a-storyboard", "comic-panel-composition-and-pacing"],
    body: (
      <>
        <p>
          Every storyboard frame answers one question: <em>what does the camera
          see?</em> Shot types are the shared vocabulary for that answer. Learn
          them and your boards become instructions a crew can follow; skip them
          and every frame becomes a conversation. Below are the shot sizes,
          angles, framings, and moves you will use most, with the abbreviations
          that appear in the notes on most professional boards.
        </p>

        <h2>Shot sizes</h2>
        <p>
          Shot size describes how much of the subject fills the frame. It is the
          single biggest lever on how close the audience feels to a character.
        </p>
        <ul>
          <li>
            <strong>Extreme wide shot (EWS)</strong> — the subject is tiny in a
            vast space. Establishes geography and scale: a lone figure on a salt
            flat, a city skyline before we go inside.
          </li>
          <li>
            <strong>Wide shot (WS)</strong> — the whole subject and a good part
            of their surroundings. Shows where everyone is relative to each other;
            often the first shot of a scene.
          </li>
          <li>
            <strong>Full shot (FS)</strong> — head to toe, filling the frame
            height. Good for body language, costume, and physical action.
          </li>
          <li>
            <strong>Medium shot (MS)</strong> — roughly from the waist up. The
            workhorse of dialogue scenes: close enough for expression, wide enough
            for gesture.
          </li>
          <li>
            <strong>Medium close-up (MCU)</strong> — chest and shoulders up. More
            intimate than a medium; common for interviews and talking-head video.
          </li>
          <li>
            <strong>Close-up (CU)</strong> — the face fills most of the frame.
            Reserved for moments where a reaction matters.
          </li>
          <li>
            <strong>Extreme close-up (ECU)</strong> — a detail: eyes, a trigger
            finger, a ringing phone. Creates intensity or points the audience at
            something they must notice.
          </li>
        </ul>

        <h2>Framings: OTS, POV, two-shots, and inserts</h2>
        <ul>
          <li>
            <strong>Over the shoulder (OTS)</strong> — framed from behind one
            character, looking at another. The standard way to cover a
            conversation and keep both people in the audience's mind.
          </li>
          <li>
            <strong>Point of view (POV)</strong> — what a character sees. Usually
            sandwiched between a shot of the character looking and a shot of their
            reaction.
          </li>
          <li>
            <strong>Two shot (2S)</strong> — two subjects in one frame, which
            emphasizes their relationship.
          </li>
          <li>
            <strong>Insert (INS)</strong> — a cutaway to an object or detail: the
            letter on the table, the clock on the wall.
          </li>
        </ul>

        <h2>Camera angles</h2>
        <p>
          Angle is where the camera sits relative to the subject. An{" "}
          <strong>eye-level</strong> shot feels neutral. A <strong>low angle</strong>,
          looking up, makes a subject loom larger and more powerful; a{" "}
          <strong>high angle</strong>, looking down, makes them smaller or more
          vulnerable. A <strong>bird's-eye</strong> view straight down turns a
          scene into a map, and a <strong>Dutch angle</strong> — the camera tilted
          on its axis — signals unease. On a board, draw the angle honestly: if the
          camera is low, show the ceiling.
        </p>

        <h2>Camera moves</h2>
        <ul>
          <li>
            <strong>Pan</strong> — the camera pivots left or right on a fixed
            point. <strong>Tilt</strong> — it pivots up or down.
          </li>
          <li>
            <strong>Push in / pull out</strong> — the camera physically moves
            toward or away from the subject (a dolly move). It builds or releases
            intensity.
          </li>
          <li>
            <strong>Zoom</strong> — the lens changes focal length while the
            camera stays put. It looks different from a push in: the background
            flattens instead of shifting, so note which one you mean.
          </li>
          <li>
            <strong>Truck</strong> — the camera slides sideways, parallel to the
            subject. <strong>Tracking</strong> follows a moving subject.
          </li>
          <li>
            <strong>Crane / jib</strong> — the camera rises or descends, often to
            reveal a location or end a scene.
          </li>
          <li>
            <strong>Handheld</strong> — deliberately unsteady, for urgency or
            documentary realism. A <strong>whip pan</strong> is a pan so fast it
            blurs, often used as a transition.
          </li>
        </ul>

        <h2>How to note shots on a board</h2>
        <p>
          Draw movement as arrows: arrows inside the frame for subject movement,
          and arrows outside or across the frame edge for camera movement. For a
          push in, draw a smaller frame inside the first one with arrows pointing
          toward it — the end framing of the move. Then write the shot size and
          move in the notes in abbreviated form, like <em>MS · Push in · 3s</em>.
        </p>
        <p>
          In <StoryboardLink />, every frame's header has a shot-type and
          camera-move picker using these same abbreviations, plus a duration
          field, so the notes read consistently across the whole board. If you
          also make comics, the same thinking shapes panels — see{" "}
          {G("comic-panel-composition-and-pacing", "panel composition and pacing")}.
        </p>
      </>
    ),
  },

  {
    slug: "storyboard-aspect-ratios",
    cluster: "storyboards",
    title: "Storyboard Aspect Ratios, Letterboxing & Crop Guides",
    icon: "crop",
    readingTime: "7 min read",
    description:
      "Which aspect ratio to storyboard in — 16:9, 2.39:1, 1.85:1, 4:3, 1:1, 4:5, or 9:16 — plus letterboxing, pillarboxing, safe areas, and how to plan one shoot for several platforms.",
    summary:
      "Choosing a frame ratio, what letterbox and pillarbox bars mean, safe areas, and protecting one shoot for vertical and widescreen.",
    related: ["what-is-a-storyboard", "camera-shot-types"],
    body: (
      <>
        <p>
          The <strong>aspect ratio</strong> is the shape of the picture: its width
          divided by its height. It is the first decision on any storyboard,
          because it sets the shape of every frame you draw. Compose a shot for a
          wide cinema screen and it will not survive being cropped to a phone, and
          the other way round. Board in the ratio you will deliver.
        </p>

        <h2>Common aspect ratios</h2>
        <ul>
          <li>
            <strong>16:9 (1.78:1)</strong> — the standard for HD and 4K video,
            YouTube, streaming, and most corporate and explainer work. If you are
            unsure, this is the default. Try the{" "}
            {T("16-9-widescreen", "16:9 widescreen storyboard template")}.
          </li>
          <li>
            <strong>1.85:1</strong> — "flat" theatrical widescreen, slightly
            wider than 16:9. Common for dramas and comedies.
          </li>
          <li>
            <strong>2.39:1</strong> — anamorphic "scope", often called 2.35 or
            2.40. Very wide, great for landscapes and for placing characters far
            apart in one frame. See the{" "}
            {T("2-39-cinemascope", "2.39:1 CinemaScope template")}.
          </li>
          <li>
            <strong>4:3 (1.33:1)</strong> — classic television, close to the
            Academy ratio (1.37:1) of older films. Used today for a deliberately
            retro or intimate feel.
          </li>
          <li>
            <strong>1:1</strong> — square, for some social feeds and ads.
          </li>
          <li>
            <strong>4:5</strong> — portrait for the Instagram feed (1080 × 1350),
            which takes up more of the screen than a square post.
          </li>
          <li>
            <strong>9:16</strong> — full-screen vertical video: Instagram Reels,
            TikTok, YouTube Shorts, and Stories. Use the{" "}
            {T("vertical-9-16", "9:16 vertical video template")}.
          </li>
        </ul>

        <h2>Letterboxing and pillarboxing</h2>
        <p>
          When a picture is wider than the screen it plays on, it is{" "}
          <strong>letterboxed</strong>: black bars fill the top and bottom. A
          2.39:1 film shown on a 16:9 TV is the classic example. When a picture
          is narrower than the screen, it is <strong>pillarboxed</strong>, with
          bars on the left and right, like a 4:3 clip in a 16:9 timeline.
        </p>
        <p>
          You can board this two ways. Either draw in frames that are already
          2.39:1, or draw in 16:9 frames with letterbox bars on them — useful when
          the final delivery is a 16:9 file with a scope image inside it, because
          the boards then match exactly what the audience will see. In{" "}
          <StoryboardLink />, the <strong>Matte</strong> setting adds 2.39:1 or
          1.85:1 letterbox bars, or 4:3 pillarbox bars, to every frame.
        </p>

        <h2>Planning one shoot for several platforms</h2>
        <p>
          Many productions now shoot once and deliver both a 16:9 version and a
          9:16 vertical cut. A vertical crop of a 16:9 frame keeps only about the
          middle third of the picture, so anything outside that band — a second
          character, a product on a table, on-screen text — disappears. Cinematographers
          call planning for this <strong>protecting</strong> for a ratio.
        </p>
        <p>
          A crop guide on your boards makes the problem visible early. Draw your
          frames at 16:9 and add a 9:16 crop guide: the area that survives the
          crop stays clear and the rest is shaded. If a shot only works in the
          shaded area, you will know to stage it differently or plan a separate
          vertical setup. Striply's Matte menu has crop guides for 9:16, 4:5, 1:1,
          and 16:9.
        </p>

        <h2>Safe areas</h2>
        <p>
          Not every pixel of a frame is guaranteed to be seen or read comfortably.{" "}
          <strong>Action safe</strong> and <strong>title safe</strong> areas are
          inset rectangles that keep important action and text away from the
          edges. Current HD broadcast guidance (SMPTE ST 2046-1) puts action safe
          at 93% of the frame and title safe at 90%; older standard-definition
          practice used 90% and 80%. Even for online video it is worth keeping
          titles inside title safe, because players and platforms overlay
          captions, progress bars, and buttons near the edges.
        </p>
        <p>
          Turn on <strong>Safe areas</strong> in Striply's storyboard toolbar to
          draw both rectangles in every frame. They print with the boards, so the
          whole team can see them. For an overview of what goes into each frame,
          read {G("what-is-a-storyboard", "what a storyboard is and how to make one")}.
        </p>
      </>
    ),
  },
];

export const GUIDES_BY_SLUG = Object.fromEntries(GUIDES.map((g) => [g.slug, g]));
